import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { connect } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { DateTime } from 'luxon'
import { format } from 'date-fns'
import {
  Button,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  EmojiTransportation as TransportationIcon,
  Close as CloseIcon,
  DirectionsCarFilledOutlined as DirectionsCarFilledOutlinedIcon,
  Today as TodayIcon,
  FileDownloadOutlined as FileDownloadOutlinedIcon,
  Person as PersonIcon,
  WatchLater as WatchLaterIcon,
  SubjectOutlined as SubjectOutlinedIcon,
  AlternateEmail as AlternateEmailIcon,
  PageviewOutlined as PageviewIcon
} from '@mui/icons-material'
import { useTranslation } from 'react-i18next'

import { api } from '../../api/api'
import SwipeableCalendar from '../../common/components/SwipeableCalendar/SwipeableCalendar'
import SwipeableTimePicker from '../../common/components/SwipeableTimePicker/SwipeableTimePicker'
import PrintVisit from '../NewVisitorPage/PrintVisit'
import { MAX_NOTES_LENGTH } from '../../variables'
import db from '../../db/db'
import { defaultCatch, defaultSuccess, setSnackbar } from '../../state/actions'
import { formatDateAndTimeToISO, formatDateToISOShort, formatDateToTimeISO, getFormateDate, getTimeDuration } from '../../common/utilities/FormatDate'
import { downloadQRAsImage, getAccessControlQR, getNormalizedEmailList, getWorkplaceReasons } from '../../common/utilities/utilities'

const getInvitationDate = invitation => invitation?.invitation_date || invitation?.date || ''
const getInvitationTime = invitation => invitation?.invitation_datetime || invitation?.invitation_time || ''

const getDateValue = value => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value

  const parsed = DateTime.fromISO(String(value), { setZone: true })
  return parsed.isValid ? parsed.toJSDate() : new Date()
}

const getTimeValue = (value, date) => {
  const parsed = DateTime.fromISO(String(value), { setZone: true })
  if (parsed.isValid) {
    const time = new Date(date)
    time.setHours(parsed.hour, parsed.minute, parsed.second, parsed.millisecond)
    return time
  }
  return date
}

const formatTimeInput = value => (
  `${String(value.getHours()).padStart(2, '0')}:${String(value.getMinutes()).padStart(2, '0')}`
)

const formatTimeRangeInput = (entryTime, exitTime) => (
  `${formatTimeInput(entryTime)} - ${formatTimeInput(exitTime)}`
)

const formatDisplayDate = value => format(value, 'MMM dd, yyyy')

const getTimeParts = value => (
  value instanceof Date
    ? [value.getHours(), value.getMinutes()]
    : value.split(':').map(Number)
)

const DetailField = ({ id, label, value, Icon, onChange, type = 'text', options }) => (
  <Stack className='pre-register-details-field' spacing={1}>
    <label htmlFor={id}>
      <Icon className='pre-register-details-field-icon' />
      <span>{label}</span>
    </label>
    <TextField
      id={id}
      fullWidth
      value={value || ''}
      type={type}
      select={Boolean(options)}
      SelectProps={options ? { inputProps: { 'aria-label': label } } : undefined}
      onChange={event => onChange?.(event.target.value)}
    >
      {options?.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
    </TextField>
  </Stack>
)

const NotesField = ({ id, label, value = '', onChange }) => (
  <Stack className='pre-register-details-notes' spacing={0.5}>
    <label htmlFor={id}>{label}</label>
    <div className='pre-register-details-notes-input'>
      <TextField
        id={id}
        fullWidth
        multiline
        rows={4}
        variant='filled'
        hiddenLabel
        value={value}
        inputProps={{ maxLength: MAX_NOTES_LENGTH }}
        onChange={event => {
          if (event.target.value.length <= MAX_NOTES_LENGTH) onChange(event.target.value)
        }}
      />
      <span className='pre-register-details-notes-counter'>{value.length}/{MAX_NOTES_LENGTH}</span>
    </div>
  </Stack>
)

const PickerDetailField = ({ id, label, value, Icon, onClick }) => (
  <Stack className='pre-register-details-field' spacing={1}>
    <label htmlFor={id}>
      <Icon className='pre-register-details-field-icon' />
      <span>{label}</span>
    </label>
    <TextField
      id={id}
      fullWidth
      value={value}
      onClick={onClick}
      InputProps={{ readOnly: true }}
    />
  </Stack>
)

function PreRegisterDetailsDialog(props) {
  const {
    ask_visit_comes_from = false,
    ask_visit_identification = false,
    ask_visit_license_plate = false,
    custom_visit_identification_label = '',
    defaultCatch,
    defaultSuccess,
    isOnline = true,
    item = {},
    onClose,
    onUpdated,
    openDeleteConfirmation = false,
    open = false,
    setSnackbar,
    visitDestinations = [],
  } = props

  const { t } = useTranslation()
  const navigate = useNavigate()
  const theme = useTheme()
  const isLarge = useMediaQuery(theme.breakpoints.up('md'))
  const [accessControlQR, setAccessControlQR] = useState(null)
  const [calendarOpen, setCalendarOpen] = useState(false)
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [resending, setResending] = useState(false)
  const [timePickerOpen, setTimePickerOpen] = useState(false)
  const [form, setForm] = useState(null)
  const [saving, setSaving] = useState(false)
  const destinationId = item.destination_id || item.destination?.id
  const destination =
    visitDestinations.find(value => String(value.id) === String(destinationId))
    || item.destination
  const enabledInvitationNotes = Boolean(destination?.enabled_invitation_notes)
  const enabledInvitationHostNotes = Boolean(destination?.enabled_invitation_host_notes)
  const reasons = getWorkplaceReasons(t)
  const reasonOptions = form?.reason && !reasons.includes(form.reason)
    ? [form.reason, ...reasons]
    : reasons
  const identificationLabel = custom_visit_identification_label || t('Identification')
  const invitationDate = getInvitationDate(item)
  const isLocalInvitation = Boolean(item?.id) && String(item.id).startsWith('local:')
  const isRemoteInvitation = Boolean(item?.id) && !String(item.id).startsWith('local:')
  const confirmationOpen = deleteConfirmationOpen || openDeleteConfirmation

  useEffect(() => {
    if (!open) return

    const date = getDateValue(invitationDate)
    const entryTime = getTimeValue(getInvitationTime(item), date)
    const duration = Number(item.duration) || 60
    const exitTime = new Date(entryTime.getTime() + duration * 60_000)

    setForm({
      identification: item.identification || '',
      name: item.first_name || '',
      lastname: item.last_name_one || item.last_name || '',
      lastnametwo: item.last_name_two || '',
      licensePlate: item.license_plate || '',
      email: item.email || '',
      comesFrom: item.comes_from || '',
      destination: item.destination_id || item.destination?.id || '',
      date,
      entryTime,
      exitTime,
      reason: item.reason || '',
      notes: item.notes || '',
      hostNotes: item.host_notes || '',
    })
  }, [invitationDate, item, open])

  const fullName = useMemo(() => {
    if (!form) return ''
    return [form.name, form.lastname, form.lastnametwo].filter(Boolean).join(' ')
  }, [form])

  const scheduledDateTime = useMemo(() => {
    if (!form) return ''
    return `${format(form.date, 'd MMMM, yyyy')} | ${format(form.entryTime, 'h:mma').toLowerCase()}`
  }, [form])

  useEffect(() => {
    if (!open) setDeleteConfirmationOpen(false)
  }, [open])

  const closeDeleteConfirmation = useCallback(() => {
    if (openDeleteConfirmation) {
      onClose?.()
      return
    }
    setDeleteConfirmationOpen(false)
  }, [onClose, openDeleteConfirmation])

  const handleDownloadQR = useCallback(() => {
    if (!form) return
    downloadQRAsImage(
      form.name || 'visitor',
      getFormateDate({ date: form.date, multiDay: false, t }),
      undefined,
      { infoMessageForDownload: t('qr_valid_for_all_days') },
    )
  }, [form, t])

  const handleDateChange = useCallback(value => {
    const nextDate = getDateValue(value)
    setForm(current => {
      const entryTime = new Date(nextDate)
      entryTime.setHours(current.entryTime.getHours(), current.entryTime.getMinutes(), 0, 0)
      const exitTime = new Date(nextDate)
      exitTime.setHours(current.exitTime.getHours(), current.exitTime.getMinutes(), 0, 0)
      return { ...current, date: nextDate, entryTime, exitTime }
    })
  }, [])

  const handleEntryTimeChange = useCallback(value => {
    const [hours, minutes] = getTimeParts(value)

    setForm(current => {
      const entryTime = new Date(current.date)
      entryTime.setHours(hours, minutes, 0, 0)
      const duration = getTimeDuration(current.entryTime, current.exitTime)
      const exitTime = new Date(entryTime.getTime() + duration * 60_000)
      return { ...current, entryTime, exitTime }
    })
  }, [])

  const handleExitTimeChange = useCallback(value => {
    const [hours, minutes] = getTimeParts(value)

    setForm(current => {
      const exitTime = new Date(current.date)
      exitTime.setHours(hours, minutes, 0, 0)
      return { ...current, exitTime }
    })
  }, [])

  const handleSaveChanges = useCallback(async () => {
    if (!form || !item?.id) return

    const normalizedEmail = getNormalizedEmailList(form.email)
    const data = {
      invitation: {
        identification: form.identification,
        first_name: form.name,
        last_name_one: form.lastname,
        last_name_two: form.lastnametwo,
        license_plate: form.licensePlate,
        email: normalizedEmail,
        comes_from: ask_visit_comes_from ? form.comesFrom : 'PARSO',
        destination_id: form.destination,
        invitation_date: formatDateToISOShort(form.date),
        invitation_time: formatDateToTimeISO(form.entryTime),
        duration: getTimeDuration(form.entryTime, form.exitTime),
        reason: form.reason,
        notes: form.notes,
        host_notes: form.hostNotes,
      }
    }

    try {
      setSaving(true)

      if (isLocalInvitation) {
        await db.invitations.put({
          ...item,
          ...data.invitation,
          date: data.invitation.invitation_date,
          invitation_time: formatDateAndTimeToISO(form.date, form.entryTime),
          syncStatus: item.syncStatus || 'pending',
          syncError: null,
          pushAttempts: 0,
        })
        setSnackbar({
          open: true,
          message: t('Pre-registration update pending sync'),
          severity: 'success',
          autoHideDuration: 4000,
        })
        await onUpdated?.()
        onClose?.()
        return
      }

      if (!isOnline) {
        await db.invitations.put({
          ...item,
          ...data.invitation,
          date: data.invitation.invitation_date,
          invitation_time: formatDateAndTimeToISO(form.date, form.entryTime),
          pendingUpdatePayload: data,
          syncOperation: 'update',
          syncStatus: 'pending',
          syncError: null,
          pushAttempts: 0,
        })
        setSnackbar({
          open: true,
          message: t('Pre-registration update pending sync'),
          severity: 'success',
          autoHideDuration: 4000,
        })
        await onUpdated?.()
        onClose?.()
        return
      }

      const response = await api.put(`/invitations/${item.id}`, data)
      await defaultSuccess(response)
      const { data: responseData = {} } = response

      if (!responseData.success) {
        setSnackbar({
          open: true,
          message: responseData?.errors?.[0] || t('Error updating pre-registration'),
          severity: 'error',
          autoHideDuration: 4000,
        })
        return
      }

      await db.invitations.put({
        ...item,
        ...responseData.invitation,
      })

      setSnackbar({
        open: true,
        message: t('Pre-registration updated successfully'),
        severity: 'success',
        autoHideDuration: 4000,
      })
      await onUpdated?.()
      onClose?.()
    } catch (error) {
      defaultCatch(error, navigate)
      setSnackbar({
        open: true,
        message: error?.response?.data?.errors?.[0] || error?.response?.data?.message || t('Error updating pre-registration'),
        severity: 'error',
        autoHideDuration: 4000,
      })
    } finally {
      setSaving(false)
    }
  }, [ask_visit_comes_from, defaultCatch, defaultSuccess, form, isLocalInvitation, isOnline, item, navigate, onClose, onUpdated, setSnackbar, t])

  const handleDelete = useCallback(async () => {
    if (!item?.id) return

    try {
      setDeleting(true)

      if (String(item.id).startsWith('local:')) {
        await db.invitations.delete(item.id)
      } else if (!isOnline) {
        await db.invitations.put({
          ...item,
          _delete: true,
          syncOperation: 'delete',
          syncStatus: 'pending',
          syncError: null,
          pushAttempts: 0,
        })
      } else {
        const response = await api.delete(`/invitations/${item.id}`)
        await defaultSuccess(response)

        if (!response?.data?.success) {
          setSnackbar({
            open: true,
            message: response?.data?.errors?.[0] || t('Error deleting pre-registration'),
            severity: 'error',
            autoHideDuration: 4000,
          })
          return
        }

        await db.invitations.delete(item.id)
      }

      setSnackbar({
        open: true,
        message: isOnline || String(item.id).startsWith('local:')
          ? t('Pre-registration deleted successfully')
          : t('Pre-registration deletion pending sync'),
        severity: 'success',
        autoHideDuration: 4000,
      })
      await onUpdated?.()
      onClose?.()
    } catch (error) {
      defaultCatch(error, navigate)
      setSnackbar({
        open: true,
        message: t('Error deleting pre-registration'),
        severity: 'error',
        autoHideDuration: 4000,
      })
    } finally {
      setDeleting(false)
    }
  }, [defaultCatch, defaultSuccess, isOnline, item, navigate, onClose, onUpdated, setSnackbar, t])

  const handleResendInvitation = useCallback(async () => {
    if (!isRemoteInvitation || !isOnline) return

    try {
      setResending(true)
      const response = await api.post(
        `/invitation/send_mail/${item.id}`,
        undefined,
        { skipClientType: true },
      )
      await defaultSuccess(response)

      if (!response?.data?.success) {
        setSnackbar({
          open: true,
          message: response?.data?.message || t('Error resending invitation'),
          severity: 'error',
          autoHideDuration: 4000,
        })
        return
      }

      setSnackbar({
        open: true,
        message: t('Invitation resent successfully'),
        severity: 'success',
        autoHideDuration: 4000,
      })
    } catch (error) {
      defaultCatch(error, navigate)
      setSnackbar({
        open: true,
        message: t('Error resending invitation'),
        severity: 'error',
        autoHideDuration: 4000,
      })
    } finally {
      setResending(false)
    }
  }, [defaultCatch, defaultSuccess, isOnline, isRemoteInvitation, item.id, navigate, setSnackbar, t])

  useEffect(() => {
    if (!open || openDeleteConfirmation || !item?.id || String(item.id).startsWith('local:')) {
      setAccessControlQR(null)
      return undefined
    }

    let cancelled = false
    setAccessControlQR(null)
    getAccessControlQR(item.id, api, {
      defaultSuccess,
      defaultCatch,
      navigate,
      setAccessControlQR: qr => {
        if (!cancelled) setAccessControlQR(qr)
      },
    })

    return () => { cancelled = true }
  }, [defaultCatch, defaultSuccess, item?.id, navigate, open, openDeleteConfirmation])

  return (
    <>
      <Dialog
        className='pre-register-details-dialog'
        fullWidth
        maxWidth='sm'
        fullScreen={!isLarge}
        open={open && !confirmationOpen}
        onClose={onClose}
        aria-labelledby='pre-register-details-title'
      >
        <DialogContent>
          <Stack className='pre-register-details-content' spacing={3}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <h2 id='pre-register-details-title'>{t('pre_registration_visitor')}</h2>
            <IconButton aria-label={t('close')} onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Divider />
          <Stack className='pre-register-details-fields'>
            {ask_visit_identification && (
              <DetailField
                id='pre-register-identification'
                label={identificationLabel}
                value={form?.identification}
                Icon={PageviewIcon}
                onChange={value => setForm(current => ({ ...current, identification: value }))}
              />
            )}
            <DetailField
              id='pre-register-name'
              label={t('name')}
              value={form?.name}
              Icon={PersonIcon}
              onChange={value => setForm(current => ({ ...current, name: value }))}
            />
            <DetailField
              id='pre-register-last-name-one'
              label={t('Last name one')}
              value={form?.lastname}
              Icon={PersonIcon}
              onChange={value => setForm(current => ({ ...current, lastname: value }))}
            />
            <DetailField
              id='pre-register-last-name-two'
              label={t('Last name two')}
              value={form?.lastnametwo}
              Icon={PersonIcon}
              onChange={value => setForm(current => ({ ...current, lastnametwo: value }))}
            />
            <DetailField
              id='pre-register-email'
              label={t('email')}
              value={form?.email}
              Icon={AlternateEmailIcon}
              onChange={value => setForm(current => ({ ...current, email: value }))}
            />
            {ask_visit_license_plate && (
              <DetailField
                id='pre-register-license-plate'
                label={t('license_plate')}
                value={form?.licensePlate}
                Icon={DirectionsCarFilledOutlinedIcon}
                onChange={value => setForm(current => ({ ...current, licensePlate: value }))}
              />
            )}
            {ask_visit_comes_from && (
              <DetailField
                id='pre-register-comes-from'
                label={t('Comes from')}
                value={form?.comesFrom}
                Icon={TransportationIcon}
                onChange={value => setForm(current => ({ ...current, comesFrom: value }))}
              />
            )}
            <PickerDetailField
              id='pre-register-date'
              label={t('date')}
              value={form ? formatDisplayDate(form.date) : ''}
              Icon={TodayIcon}
              onClick={() => setCalendarOpen(true)}
            />
            <SwipeableCalendar
              type='basic'
              open={calendarOpen}
              date={form?.date || new Date()}
              setOpen={setCalendarOpen}
              onChange={handleDateChange}
              onContinue={() => setCalendarOpen(false)}
            />
            <PickerDetailField
              id='pre-register-time'
              label={t('hour')}
              value={form ? formatTimeRangeInput(form.entryTime, form.exitTime) : ''}
              Icon={WatchLaterIcon}
              onClick={() => setTimePickerOpen(true)}
            />
            <SwipeableTimePicker
              type='range'
              open={timePickerOpen}
              setOpen={setTimePickerOpen}
              showAllTime={false}
              allTime={false}
              time={form?.entryTime || new Date()}
              exitTime={form?.exitTime || new Date()}
              setTime={handleEntryTimeChange}
              setExitTime={handleExitTimeChange}
              onContinue={() => setTimePickerOpen(false)}
            />
            <DetailField
              id='pre-register-reason'
              label={t('reason')}
              value={form?.reason}
              Icon={SubjectOutlinedIcon}
              options={reasonOptions}
              onChange={value => setForm(current => ({ ...current, reason: value }))}
            />
            {(enabledInvitationNotes || enabledInvitationHostNotes) && (
              <Stack spacing={2}>
                {enabledInvitationNotes && (
                  <NotesField
                    id='pre-register-notes'
                    label={`${t('preregistration_reception_notes')} ${t('(optional)')}`}
                    value={form?.notes || ''}
                    onChange={notes => setForm(current => ({ ...current, notes }))}
                  />
                )}
                {enabledInvitationHostNotes && (
                  <NotesField
                    id='pre-register-host-notes'
                    label={t('Notes for visit')}
                    value={form?.hostNotes || ''}
                    onChange={hostNotes => setForm(current => ({ ...current, hostNotes }))}
                  />
                )}
              </Stack>
            )}
          </Stack>
          <div className='pre-register-details-qr'>
            {accessControlQR && (
              <img src={accessControlQR} alt={t('Access control QR')} />
            )}
          </div>
          {form && accessControlQR && (
            <div aria-hidden='true' style={{ position: 'fixed', left: '-10000px', top: 0, pointerEvents: 'none' }}>
              <PrintVisit
                qr={accessControlQR}
                item={{ first_name: form.name, last_name_one: form.lastname, last_name_two: form.lastnametwo }}
                date={form.date}
                entry_time={form.entryTime}
                exit_time={form.exitTime}
                multiDay={false}
                notes={form.notes}
                hostNotes={form.hostNotes}
              />
            </div>
          )}
          <Stack className='pre-register-details-actions' spacing={2}>
            <Button
              type='button'
              variant='outlined'
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={!accessControlQR}
              onClick={handleDownloadQR}
            >
              {t('Download QR')}
            </Button>
            <Button
              type='button'
              variant='outlined'
              disabled={!isRemoteInvitation || !isOnline || resending}
              onClick={handleResendInvitation}
            >
              {t('Resend invitation to visitor')}
            </Button>
            <Button
              type='button'
              variant='contained'
              disableElevation
              disabled={!item?.id || saving}
              onClick={handleSaveChanges}
            >
              {t('Save changes')}
            </Button>
            <Button
              type='button'
              variant='outlined'
              color='error'
              disabled={!item?.id || deleting}
              onClick={() => setDeleteConfirmationOpen(true)}
            >
              {t('Delete pre-registration')}
            </Button>
          </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
      <Dialog
        className='pre-register-delete-confirmation-dialog'
        fullWidth
        maxWidth='sm'
        fullScreen={!isLarge}
        open={open && confirmationOpen}
        onClose={closeDeleteConfirmation}
        aria-labelledby='pre-register-delete-confirmation-title'
      >
        <DialogContent>
          <Stack className='pre-register-delete-confirmation-content'>
            <h2 id='pre-register-delete-confirmation-title'>
              {t('Delete pre-registration')}
            </h2>
            <Divider />
            <Stack className='pre-register-delete-confirmation-copy' spacing={1}>
              <p>{t('You are about to delete the following pre-registration:')}</p>
              <p>
                <strong>{fullName}</strong>
                <strong>{scheduledDateTime}</strong>
              </p>
            </Stack>
            <Stack className='pre-register-delete-confirmation-actions' direction='row' spacing={2}>
              <Button
                type='button'
                variant='outlined'
                disabled={deleting}
                onClick={closeDeleteConfirmation}
                size='small'
              >
                {t('go back')}
              </Button>
              <Button
                type='button'
                variant='contained'
                color='error'
                disableElevation
                disabled={deleting}
                onClick={handleDelete}
                size='small'
              >
                {t('remove')}
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  )
}

const mapStateToProps = state => ({
  ask_visit_comes_from: state.profile.ask_visit_comes_from,
  ask_visit_identification: state.profile.ask_visit_identification,
  ask_visit_license_plate: state.profile.ask_visit_license_plate,
  custom_visit_identification_label: state.profile.custom_visit_identification_label,
  isOnline: state.utilities.isOnline,
  visitDestinations: state.profile.visitDestinations,
})

const mapDispatchToProps = {
  defaultCatch,
  defaultSuccess,
  setSnackbar,
}

export default connect(mapStateToProps, mapDispatchToProps)(PreRegisterDetailsDialog)
